import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApi } from '../../lib/useApi.js';
import { PageViewerControls } from '../../components/index.js';
import {
  AiCritiquePanel,
  AutomationProofDrawer,
  CurrentStateCards,
  DiagnosisSectionNav,
  DiagnosisSummaryPanel,
  FishboneSection,
  MeasuresPanel,
  ProcessSipocSection,
  RootCauseSection,
} from './DiagnosisSections.jsx';
import {
  BoardPacketView,
  EvidenceLineageView,
  GovernanceDecisionsView,
  GovernanceSectionNav,
  GovernanceSummaryPanel,
  GovernanceSummaryView,
  deriveBriefStatus,
  deriveCloseoutGates,
  deriveDecisionsNeeded,
  deriveReadoutStatus,
} from './GovernanceSections.jsx';
import {
  BriefSafeNowSection,
  BowlerTrendSection,
  CostProofSection,
  MetricsSectionNav,
  MetricsValueSummaryPanel,
  SustainmentSection,
  TrustGapsSection,
  deriveMetricTrust,
} from './MetricsSections.jsx';
import {
  CloseoutChecklistView,
  CloseoutGraduationLineageView,
  CloseoutHandoffPacketView,
  CloseoutLessonsView,
  CloseoutReadinessSummary,
  CloseoutReadinessView,
  CloseoutSectionNav,
  CloseoutValidationView,
  deriveRequiredGatesOpen,
  groupCloseoutChecklist,
} from './CloseoutSections.jsx';
import {
  CANVAS_DEFAULT_SECTION,
  CanvasSectionNav,
  CanvasSummaryPanel,
  EditCanvasView,
  EvidenceAssumptionsView,
  ScopePlacematView,
  VersionHistoryView,
} from './CanvasSections.jsx';
import {
  QA_DEFAULT_SECTION,
  ConversionCenterView,
  LeadershipImpactView,
  QaSectionNav,
  QaSummaryPanel,
  QaTraceabilityView,
  ReadinessChecksView,
  deriveQaConversionState,
} from './ReadinessQaSections.jsx';
import {
  RoadmapCatalogView,
  RoadmapSectionNav,
  RoadmapSummaryPanel,
  RoadmapSummaryView,
  deriveShellTiers,
  summarizeTiers,
} from './RoadmapScopeSections.jsx';
import {
  RapidBowlerGrid,
  RapidDriverDiagram,
  RapidLineagePreview,
  RapidMetricsProofTable,
} from './RapidSharedChrome.jsx';
import {
  acceptedCloseoutStatus,
  buildCloseoutChecklist,
  buildDerivedAssumptions,
  buildDerivedBarriers,
  buildDerivedDataIssues,
  buildDerivedDecisions,
  buildDetailSearch,
  buildDriverNodes,
  buildMetricsTrustGaps,
  nowTimestamp,
  parseLines,
  podDisplayName,
} from './rapidPageUtils.js';

export function DiagnosisTab({ detail, onOpenEvidence }) {
  const diagnosis = detail.rapid;
  const [searchParams, setSearchParams] = useSearchParams();
  const deterministicNodes = (detail.pods || [])
    .filter((pod) => ['POD-1', 'POD-2'].includes(pod.id))
    .flatMap((pod) =>
      buildDriverNodes(pod)
        .filter((node) =>
          ['P1-D5', 'P1-D4', 'P1-D8', 'P2-D6', 'P2-D7', 'P2-D8', 'P2-D11'].includes(node.id),
        )
        .map((node) => ({ ...node, podLabel: podDisplayName(pod.id) })),
    );
  const [activeSection, setActiveSection] = useState('summary');
  const shows = (key) => activeSection === 'all' || activeSection === key;
  return (
    <div className="rapid-tab-panel diagnosis-tab-panel" data-testid="rapid-diagnosis-tab">
      <DiagnosisSectionNav active={activeSection} onSelect={setActiveSection} />
      <div className="diagnosis-views">
        <div className="diagnosis-view" hidden={!shows('summary')}>
          <DiagnosisSummaryPanel storyboard={diagnosis.storyboard} />
        </div>
        <div className="diagnosis-view" hidden={!shows('driver')}>
          <RapidDriverDiagram detail={detail} onOpenEvidence={onOpenEvidence} />
        </div>
        <div className="diagnosis-view" hidden={!shows('current-state')}>
          <CurrentStateCards
            cards={diagnosis.storyboard?.currentStateCards}
            title={diagnosis.currentState?.title}
          />
        </div>
        <div className="diagnosis-view" hidden={!shows('root-cause')}>
          <RootCauseSection fiveWhys={diagnosis.fiveWhys} />
        </div>
        <div className="diagnosis-view" hidden={!shows('fishbone')}>
          <FishboneSection
            fishbone={diagnosis.fishbone}
            fishboneEffect={diagnosis.fishboneEffect}
          />
        </div>
        <div className="diagnosis-view" hidden={!shows('process')}>
          <ProcessSipocSection sipoc={diagnosis.sipoc} processMap={diagnosis.processMap} />
        </div>
        <div className="diagnosis-view" hidden={!shows('measures')}>
          <MeasuresPanel
            measures={diagnosis.measuresOfSuccess}
            bowler={diagnosis.bowlerChart}
            onOpenMetrics={() => setSearchParams(buildDetailSearch(searchParams, 'metrics'))}
          />
        </div>
        <div className="diagnosis-view" hidden={!shows('ai-critique')}>
          <AiCritiquePanel aiCritique={diagnosis.aiCritique} />
        </div>
      </div>
      <AutomationProofDrawer nodes={deterministicNodes} />
    </div>
  );
}

export function QaTab({ detail, detailState, onStateChange }) {
  const findings = detail.rapid.readinessQa?.findings || [];
  const [activeSection, setActiveSection] = useState(QA_DEFAULT_SECTION);

  function convertFinding(finding, targetType) {
    const existing = (detailState.conversions || []).find(
      (entry) => entry.findingId === finding.id && entry.targetType === targetType,
    );
    if (existing) return;
    const recordId = `${targetType.toUpperCase().replace(/[^A-Z]/g, '')}-${finding.id}`;
    const conversion = {
      findingId: finding.id,
      targetType,
      recordId,
      title: finding.title,
      barrierSubtype: targetType === 'Barrier' ? finding.barrierSubtype : null,
    };
    const nextState = {
      ...detailState,
      conversions: [...(detailState.conversions || []), conversion],
      createdActions: detailState.createdActions || [],
      createdRfIs: detailState.createdRfIs || [],
      createdDecisions: detailState.createdDecisions || [],
      createdAssumptions: detailState.createdAssumptions || [],
      createdBarriers: detailState.createdBarriers || [],
      createdDataIssues: detailState.createdDataIssues || [],
    };
    if (targetType === 'Action') {
      nextState.createdActions = [
        ...(detailState.createdActions || []),
        {
          id: recordId,
          rapidId: detail.rapid.id,
          podId: null,
          title: `Converted from ${finding.id}: ${finding.title}`,
          type: 'rapid_action',
          status: 'todo',
          displayStatus: 'To Do',
          kanbanColumn: 'todo',
          ownerUserId: detail.rapid.functionalSponsorUserId,
          ownerLabel: detail.rapid.functionalSponsorLabel || 'Functional Sponsor',
          dueDate: detail.rapid.timeline?.targetCloseDate,
          priority: 'medium',
          percentComplete: 0,
          blockedReason: null,
          source: finding.id,
          health: 'amber',
        },
      ];
    }
    if (targetType === 'RFI') {
      nextState.createdRfIs = [
        ...(detailState.createdRfIs || []),
        {
          id: recordId,
          q: finding.proposedResolution,
          to: detail.rapid.functionalSponsorLabel || 'Functional Sponsor',
          sourceFindingId: finding.id,
        },
      ];
    }
    if (targetType === 'Decision') {
      nextState.createdDecisions = [
        ...(detailState.createdDecisions || []),
        {
          id: recordId,
          rapidId: detail.rapid.id,
          title: `Decision from ${finding.id}`,
          decisionType: 'qa-converted',
          occurredAt: new Date().toISOString(),
          rationale: finding.proposedResolution,
          impact: finding.category,
          sourceFindingId: finding.id,
        },
      ];
    }
    if (targetType === 'Assumption') {
      nextState.createdAssumptions = [
        ...(detailState.createdAssumptions || []),
        {
          id: recordId,
          text: finding.proposedResolution,
          sourceFindingId: finding.id,
          status: 'Accepted assumption',
        },
      ];
    }
    if (targetType === 'Barrier') {
      nextState.createdBarriers = [
        ...(detailState.createdBarriers || []),
        {
          id: recordId,
          barrier: finding.title,
          owner: detail.rapid.functionalSponsorLabel || 'Functional Sponsor',
          status: 'Open',
          severity: finding.severity,
          decisionNeeded: finding.proposedResolution,
          barrierSubtype: finding.barrierSubtype || null,
          sourceFindingId: finding.id,
        },
      ];
    }
    if (targetType === 'Data Issue') {
      nextState.createdDataIssues = [
        ...(detailState.createdDataIssues || []),
        {
          id: recordId,
          title: finding.title,
          summary: finding.proposedResolution,
          status: 'Needs review',
          sourceFindingId: finding.id,
        },
      ];
    }
    const nextEntries = [
      {
        ts: nowTimestamp(),
        actor: 'Local operator',
        event: `${finding.id} converted to ${targetType}.`,
      },
      ...(detailState.activityEntries || []),
    ];
    onStateChange({
      ...nextState,
      activityEntries: nextEntries,
    });
  }

  const conversionState = deriveQaConversionState(findings, detailState);
  const shows = (key) => activeSection === 'all' || activeSection === key;

  return (
    <div className="rapid-tab-panel qa-tab-panel" data-testid="rapid-qa-tab">
      <QaSummaryPanel findings={findings} conversionState={conversionState} />

      <QaSectionNav active={activeSection} onSelect={setActiveSection} />

      <div className="qa-views">
        <div className="qa-view" hidden={!shows('checks')}>
          <ReadinessChecksView
            findings={findings}
            conversionState={conversionState}
            onConvert={convertFinding}
          />
        </div>
        <div className="qa-view" hidden={!shows('conversions')}>
          <ConversionCenterView conversionState={conversionState} />
        </div>
        <div className="qa-view" hidden={!shows('impact')}>
          <LeadershipImpactView findings={findings} conversionState={conversionState} />
        </div>
        <div className="qa-view" hidden={!shows('traceability')}>
          <QaTraceabilityView findings={findings} conversionState={conversionState} />
        </div>
      </div>
    </div>
  );
}

export function CanvasTab({ detail, detailState, onStateChange }) {
  const rapid = detail.rapid;
  const canvas = rapid.canvas || {};
  const current = detailState.canvas;
  const [activeSection, setActiveSection] = useState(CANVAS_DEFAULT_SECTION);
  const shows = (key) => activeSection === 'all' || activeSection === key;

  const assumptions = buildDerivedAssumptions(rapid, detailState);
  const barriers = buildDerivedBarriers(rapid, detailState);
  const scopeInItems = parseLines(current.scopeInText);
  const scopeOutItems = parseLines(current.scopeOutText);

  function updateCanvas(patch) {
    onStateChange({
      ...detailState,
      canvas: { ...current, ...patch },
    });
  }

  function saveHistoryEntry() {
    updateCanvas({
      history: [
        {
          version: 'Saved draft',
          date: nowTimestamp(),
          note: 'Saved draft',
          scopeIn: scopeInItems,
          scopeOut: scopeOutItems,
        },
        ...(current.history || []),
      ],
    });
  }

  const placematData = {
    title: rapid.title,
    asOfDate: canvas.asOfDate,
    problemStatement: current.problemStatement,
    measureOfPerformance: canvas.measureOfPerformance,
    scopeInItems,
    scopeOutItems,
    northStar: current.northStar,
    northStarTargets: canvas.northStarTargets,
    c2: canvas.c2,
    alignmentWithRelatedEfforts: canvas.alignmentWithRelatedEfforts,
    assumptions,
    rootCauses: canvas.rootCauses,
    performanceMeasurement: canvas.performanceMeasurement,
    milestones: canvas.milestones,
    barriers,
    evidenceLinks: canvas.evidenceLinks,
  };

  return (
    <div className="rapid-tab-panel canvas-tab-panel" data-testid="rapid-canvas-tab">
      <CanvasSummaryPanel
        hasProblem={Boolean(current.problemStatement?.trim())}
        hasNorthStar={Boolean(current.northStar?.trim())}
        scopeInCount={scopeInItems.length}
        scopeOutCount={scopeOutItems.length}
        assumptionCount={assumptions.length}
        evidenceCount={(canvas.evidenceLinks || []).length}
        lastVersion={(current.history || [])[0]?.version}
      />

      <CanvasSectionNav active={activeSection} onSelect={setActiveSection} />

      <div className="canvas-views">
        <div className="canvas-view" hidden={!shows('brief')}>
          <ScopePlacematView data={placematData} />
        </div>
        <div className="canvas-view" hidden={!shows('edit')}>
          <EditCanvasView
            current={current}
            expected={rapid.expectedExtraction}
            onUpdate={updateCanvas}
            onSaveHistory={saveHistoryEntry}
          />
        </div>
        <div className="canvas-view" hidden={!shows('evidence')}>
          <EvidenceAssumptionsView evidenceLinks={canvas.evidenceLinks} assumptions={assumptions} />
        </div>
        <div className="canvas-view" hidden={!shows('history')}>
          <VersionHistoryView history={current.history} />
        </div>
      </div>
    </div>
  );
}

function displayFinancialValue(record, keys) {
  const key = keys.find((candidate) => record?.[candidate] != null);
  const value = key ? record[key] : null;
  if (value == null || value === '') return '\u2014';
  if (key.endsWith('UsdM') && Number.isFinite(Number(value))) {
    return `$${Number(value).toLocaleString()}M`;
  }
  return typeof value === 'number' ? `$${value.toLocaleString()}` : String(value);
}

function RapidFinancialRecordsTable({ records }) {
  return (
    <div className="rapid-table-scroll rapid-scroll-x" tabIndex={0}>
      <table className="table" aria-label="Financial records" data-testid="rapid-financial-table">
        <thead>
          <tr>
            <th>Record</th>
            <th>FY / period</th>
            <th>Category</th>
            <th>Planned</th>
            <th>Expended</th>
            <th>Forecast</th>
            <th>Cost / value</th>
            <th>Confidence / source</th>
          </tr>
        </thead>
        <tbody>
          {records.map((record) => (
            <tr key={record.id}>
              <td>{record.id}</td>
              <td>{[record.fiscalYear, record.period].filter(Boolean).join(' / ') || '\u2014'}</td>
              <td>{record.category || '\u2014'}</td>
              <td>
                {displayFinancialValue(record, ['plannedBudgetUsdM', 'plannedBudget', 'planned'])}
              </td>
              <td>
                {displayFinancialValue(record, ['actualSpendUsdM', 'actualSpend', 'expended'])}
              </td>
              <td>{displayFinancialValue(record, ['forecastAtCompletionUsdM', 'forecast'])}</td>
              <td>
                {displayFinancialValue(record, [
                  'totalCostAvoidance',
                  'displayValue',
                  'amount',
                  'value',
                ])}
              </td>
              <td>{record.confidence || record.sourceStatus || record.source || '\u2014'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function MetricsTab({ detail, detailState }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sourceStatus, setSourceStatus] = useState('All');
  const [measureType, setMeasureType] = useState('All');
  const [search, setSearch] = useState('');
  const [activeSection, setActiveSection] = useState('summary');
  const [showAllMetrics, setShowAllMetrics] = useState(false);
  const [showAllFinancials, setShowAllFinancials] = useState(false);
  const [metricsOffset, setMetricsOffset] = useState(0);
  const [financialsOffset, setFinancialsOffset] = useState(0);
  const [briefConfirmedOffset, setBriefConfirmedOffset] = useState(0);
  const [briefNeedsOffset, setBriefNeedsOffset] = useState(0);

  const rapid = detail.rapid;
  const briefSafeVisible = activeSection === 'brief-safe' || activeSection === 'all';
  const metricsPage = useApi(`/rapids/${rapid.id}/mission-kpis`, {
    query: {
      limit: 25,
      offset: metricsOffset,
      ...(sourceStatus !== 'All' ? { sourceStatus } : {}),
      ...(measureType !== 'All' ? { measureType } : {}),
      ...(search.trim() ? { search: search.trim() } : {}),
    },
    skip: false,
  });
  const financialsPage = useApi(`/rapids/${rapid.id}/financial-records`, {
    query: { limit: 25, offset: financialsOffset },
    skip: !showAllFinancials,
  });
  const briefConfirmedPage = useApi(`/rapids/${rapid.id}/mission-kpis`, {
    query: { limit: 25, offset: briefConfirmedOffset, sourceStatus: 'Confirmed' },
    skip: !briefSafeVisible,
  });
  const briefNeedsPage = useApi(`/rapids/${rapid.id}/mission-kpis`, {
    query: { limit: 25, offset: briefNeedsOffset, sourceStatus: 'Needs confirmation' },
    skip: !briefSafeVisible,
  });
  const pageMetrics = metricsPage.data?.data || [];
  const metrics = showAllMetrics ? pageMetrics : detail.metrics || [];
  const financials = showAllFinancials ? financialsPage.data?.data || [] : detail.financials || [];
  const metricsTotal = Number(detail.previews?.metrics?.total ?? metrics.length);
  const financialsTotal = Number(detail.previews?.financials?.total ?? financials.length);
  const previewTrust = deriveMetricTrust(detail.metrics || []);
  const confirmedExamples = briefConfirmedPage.data?.data || previewTrust.confirmed;
  const needsExamples = briefNeedsPage.data?.data || previewTrust.needsConfirmation;
  const derivedTrust = {
    confirmed: confirmedExamples,
    needsConfirmation: needsExamples,
    dataDefinition: needsExamples.filter(
      (metric) => String(metric.measureType).toLowerCase() === 'data definition',
    ),
  };
  const trustSummary = metricsPage.data?.trustSummary;
  const trust = {
    ...derivedTrust,
    counts: {
      confirmed: Number(trustSummary?.confirmed ?? derivedTrust.confirmed.length),
      needsConfirmation: Number(
        trustSummary?.needsConfirmation ?? derivedTrust.needsConfirmation.length,
      ),
      dataDefinition: Number(trustSummary?.dataDefinition ?? derivedTrust.dataDefinition.length),
    },
  };
  const cadence = rapid.closeout?.sustainmentCadence;
  const costAvoidance = rapid.costAvoidance;
  const dataIssues = buildDerivedDataIssues(detailState);
  const trustGaps = buildMetricsTrustGaps(detail, trust, dataIssues);

  const filtered = metrics.filter((metric) => {
    if (showAllMetrics) return true;
    if (sourceStatus !== 'All' && metric.sourceStatus !== sourceStatus) return false;
    if (measureType !== 'All' && metric.measureType !== measureType) return false;
    if (!search.trim()) return true;
    return `${metric.id} ${metric.name}`.toLowerCase().includes(search.trim().toLowerCase());
  });

  const shows = (key) => activeSection === 'all' || activeSection === key;

  return (
    <div className="rapid-tab-panel metrics-tab-panel" data-testid="rapid-metrics-tab">
      <MetricsValueSummaryPanel
        valueSummary={rapid.valueSummary}
        costAvoidance={costAvoidance}
        trust={trust}
        cadence={cadence}
        confidence={costAvoidance?.confidence}
      />

      <MetricsSectionNav active={activeSection} onSelect={setActiveSection} />

      <div className="metrics-views">
        <div className="metrics-view" hidden={!shows('summary')}>
          <section className="rapid-panel metrics-summary-view" data-testid="metrics-summary-view">
            <div className="rapid-section-heading">
              <div>
                <div className="eyebrow">Start here</div>
                <h3>What you can brief today</h3>
              </div>
            </div>
            <p>
              {trust.counts.confirmed} of {metricsTotal} metrics carry a confirmed source and are
              brief-safe as briefing evidence. {trust.counts.needsConfirmation} still need
              confirmation (including {trust.counts.dataDefinition} data-definition items) before a
              production value claim.
            </p>
            <ul className="rapid-inline-list">
              <li>
                <strong>Brief-Safe Now</strong> — confirmed vs needs-confirmation metrics.
              </li>
              <li>
                <strong>Cost Proof</strong> — how the {costAvoidance?.displayValue || '$294K'} value
                is modeled.
              </li>
              <li>
                <strong>Bowler Trend</strong> — virtual PHA utilization vs FY26 target.
              </li>
              <li>
                <strong>Trust Gaps</strong> — what must be resolved before production validation.
              </li>
            </ul>
            <span className="chip outline sm">
              Sustainment cadence: {cadence || '30 / 60 / 90 days, then quarterly'}
            </span>
          </section>
        </div>

        <div className="metrics-view" hidden={!shows('brief-safe')}>
          <BriefSafeNowSection
            trust={trust}
            confirmedPage={briefConfirmedPage.data?.page}
            needsPage={briefNeedsPage.data?.page}
            onConfirmedOffsetChange={setBriefConfirmedOffset}
            onNeedsOffsetChange={setBriefNeedsOffset}
          />
        </div>

        <div className="metrics-view" hidden={!shows('cost-proof')}>
          <CostProofSection costAvoidance={costAvoidance} />
        </div>

        <div className="metrics-view" hidden={!shows('bowler')}>
          <BowlerTrendSection bowlerChart={rapid.bowlerChart}>
            <RapidBowlerGrid bowlerChart={rapid.bowlerChart} />
          </BowlerTrendSection>
        </div>

        <div className="metrics-view" hidden={!shows('library')}>
          <section
            className="rapid-panel metrics-library-section"
            data-testid="metrics-library-section"
          >
            <div className="rapid-section-heading">
              <div>
                <div className="eyebrow">Metrics library</div>
                <h3>Detailed metric source of truth</h3>
              </div>
            </div>
            <div className="rapid-toolbar">
              <label>
                Source status
                <select
                  data-testid="filter-metrics-sourcestatus"
                  value={sourceStatus}
                  onChange={(event) => {
                    setSourceStatus(event.target.value);
                    setMetricsOffset(0);
                    setShowAllMetrics(true);
                  }}
                >
                  <option value="All">All</option>
                  <option value="Confirmed">Confirmed</option>
                  <option value="Needs confirmation">Needs confirmation</option>
                </select>
              </label>
              <label>
                Measure type
                <select
                  data-testid="filter-metrics-measuretype"
                  value={measureType}
                  onChange={(event) => {
                    setMeasureType(event.target.value);
                    setMetricsOffset(0);
                    setShowAllMetrics(true);
                  }}
                >
                  <option value="All">All</option>
                  <option value="MOE">MOE</option>
                  <option value="MOP">MOP</option>
                  <option value="Data Definition">Data Definition</option>
                </select>
              </label>
              <label className="rapid-toolbar-search">
                Search
                <input
                  data-testid="search-metrics"
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                    setMetricsOffset(0);
                    setShowAllMetrics(true);
                  }}
                  placeholder="Search metrics"
                />
              </label>
              <button
                className="btn ghost sm"
                data-testid="filter-clear"
                type="button"
                onClick={() => {
                  setSourceStatus('All');
                  setMeasureType('All');
                  setSearch('');
                  setMetricsOffset(0);
                }}
              >
                Clear filters
              </button>
            </div>
            <div className="rapid-chip-row">
              <span className="chip outline sm" data-testid="metrics-library-result-count">
                {showAllMetrics
                  ? `Showing ${filtered.length ? metricsOffset + 1 : 0}\u2013${metricsOffset + filtered.length} of ${Number(metricsPage.data?.page?.total ?? metricsTotal)} metrics`
                  : `Showing ${filtered.length} of ${metricsTotal} metrics`}
              </span>
              <span className="chip outline sm">{trust.counts.confirmed} confirmed</span>
              <span className="chip outline sm">
                {trust.counts.needsConfirmation} need confirmation
              </span>
            </div>
            {detail.previews?.metrics?.truncated || showAllMetrics ? (
              <button
                type="button"
                className="btn ghost sm"
                onClick={() => {
                  setMetricsOffset(0);
                  // Returning to preview clears the filters (F-G09-002): preview is an unfiltered
                  // first-N glance, so its count stays truthful and never pairs a filtered
                  // numerator with the unfiltered total. This mirrors the existing symmetry where
                  // changing a filter forces full mode.
                  if (showAllMetrics) {
                    setSourceStatus('All');
                    setMeasureType('All');
                    setSearch('');
                  }
                  setShowAllMetrics((value) => !value);
                }}
              >
                {showAllMetrics ? 'Show metrics preview' : `View All ${metricsTotal} metrics`}
              </button>
            ) : null}
            <div className="rapid-chip-row" data-testid="rapid-financial-records-total">
              <span className="chip outline sm">
                {showAllFinancials
                  ? `Showing ${financials.length ? financialsOffset + 1 : 0}\u2013${financialsOffset + financials.length} of ${Number(financialsPage.data?.page?.total ?? financialsTotal)} financial records`
                  : `Showing ${financials.length} of ${financialsTotal} financial records`}
              </span>
              {detail.previews?.financials?.truncated || showAllFinancials ? (
                <button
                  type="button"
                  className="btn ghost sm"
                  onClick={() => {
                    setFinancialsOffset(0);
                    setShowAllFinancials((value) => !value);
                  }}
                >
                  {showAllFinancials
                    ? 'Show financial preview'
                    : `View All ${financialsTotal} financial records`}
                </button>
              ) : null}
            </div>
            {filtered.length ? (
              <RapidMetricsProofTable metrics={filtered} />
            ) : (
              <div className="rapid-empty-inline" data-testid="empty-metrics-filtered">
                No metrics match the current filters.
              </div>
            )}
            {showAllMetrics && metricsPage.data?.page ? (
              <PageViewerControls
                page={metricsPage.data.page}
                pageSize={25}
                pageSizeOptions={[25]}
                label="metrics"
                onOffsetChange={setMetricsOffset}
              />
            ) : null}
            {financials.length ? <RapidFinancialRecordsTable records={financials} /> : null}
            {showAllFinancials && financialsPage.data?.page ? (
              <PageViewerControls
                page={financialsPage.data.page}
                pageSize={25}
                pageSizeOptions={[25]}
                label="financial records"
                onOffsetChange={setFinancialsOffset}
              />
            ) : null}
          </section>
        </div>

        <div className="metrics-view" hidden={!shows('trust-gaps')}>
          <TrustGapsSection gaps={trustGaps} />
        </div>

        <div className="metrics-view" hidden={!shows('sustainment')}>
          <SustainmentSection
            cadence={cadence}
            sustainmentOwner={rapid.closeout?.sustainmentOwner}
            handoffOwner={rapid.closeout?.handoffOwner}
            onOpenCloseout={() => setSearchParams(buildDetailSearch(searchParams, 'closeout'))}
          />
        </div>
      </div>
    </div>
  );
}

const GOVERNANCE_SAY_CAVEAT_ASK = {
  say: 'This RAPID is directionally briefable as a proof of value, diagnosis, pod execution, and handoff lineage.',
  caveat:
    'Production validation still depends on denominator ownership, source-system authority, and sponsor validation.',
  ask: 'Confirm the remaining decision questions and move the record toward closeout / handoff readiness.',
};

export function GovernanceTab({
  detail,
  detailState,
  onGenerateBrief,
  generatingBrief,
  generatedBrief,
  onOpenEvidence,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeSection = searchParams.get('gv') || 'summary';
  const setActiveSection = (key) => {
    const next = new URLSearchParams(searchParams);
    if (key === 'summary') next.delete('gv');
    else next.set('gv', key);
    setSearchParams(next, { replace: true });
  };

  const rapid = detail.rapid;
  const barriers = buildDerivedBarriers(rapid, detailState);
  const openBarriers = barriers.filter((barrier) => ['Open', 'Watch'].includes(barrier.status));
  const decisionsNeeded = deriveDecisionsNeeded(barriers);
  const decisionsMade = buildDerivedDecisions(detail, detailState);
  const closeout = rapid.closeout;
  const validationStatus =
    detailState?.closeout?.validationStatus || closeout?.sponsorValidation || 'Pending';
  const validationPending = !acceptedCloseoutStatus(validationStatus);
  const closeoutGates = deriveCloseoutGates(closeout, validationStatus);
  const closeoutReadiness = closeout?.closeoutReadiness || {};
  const briefStatus = deriveBriefStatus(rapid.roleViews, detail.briefings, generatedBrief);
  const readoutStatus = deriveReadoutStatus({
    validationPending,
    openDecisions: decisionsNeeded.length,
    briefGenerated: Boolean(generatedBrief),
    gatesRemaining: closeoutGates.length,
  });

  const stats = {
    decisionsNeeded: decisionsNeeded.length,
    openBlockers: openBarriers.length,
    briefStatus,
    closeoutPercent: closeoutReadiness.percent ?? 0,
    closeoutComplete: closeoutReadiness.complete ?? 0,
    closeoutTotal: closeoutReadiness.total ?? 0,
  };

  const openCloseout = () => setSearchParams(buildDetailSearch(searchParams, 'closeout'));
  const nextCheckpoint = `Sponsor validation: ${validationStatus}. Clear ${closeoutGates.length} required closeout gate${
    closeoutGates.length === 1 ? '' : 's'
  } before handoff readiness.`;

  const shows = (key) => activeSection === 'all' || activeSection === key;

  return (
    <div className="rapid-tab-panel governance-tab-panel" data-testid="rapid-governance-tab">
      <GovernanceSummaryPanel
        stats={stats}
        readoutStatus={readoutStatus}
        sayCaveatAsk={GOVERNANCE_SAY_CAVEAT_ASK}
      />

      <GovernanceSectionNav active={activeSection} onSelect={setActiveSection} />

      <div className="governance-views">
        <div className="governance-view" hidden={!shows('summary')}>
          <GovernanceSummaryView
            sayCaveatAsk={GOVERNANCE_SAY_CAVEAT_ASK}
            nextCheckpoint={nextCheckpoint}
            onOpenCloseout={openCloseout}
          />
        </div>

        <div className="governance-view" hidden={!shows('decisions')}>
          <GovernanceDecisionsView
            decisionsNeeded={decisionsNeeded}
            closeoutGates={closeoutGates}
            decisionsMade={decisionsMade}
            onOpenCloseout={openCloseout}
          />
        </div>

        <div className="governance-view" hidden={!shows('board-packet')}>
          <BoardPacketView
            detail={detail}
            barriers={barriers}
            decisionsMade={decisionsMade}
            onGenerateBrief={onGenerateBrief}
            generatingBrief={generatingBrief}
            generatedBrief={generatedBrief}
          />
        </div>

        <div className="governance-view" hidden={!shows('evidence-lineage')}>
          <EvidenceLineageView
            briefings={detail.briefings}
            evidenceLinks={rapid.canvas?.evidenceLinks}
          >
            <RapidLineagePreview detail={detail} onOpenEvidence={onOpenEvidence} />
          </EvidenceLineageView>
        </div>
      </div>
    </div>
  );
}

export function CloseoutTab({
  detail,
  detailState,
  onStateChange,
  onValidate,
  validating,
  onOpenEvidence,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeSection = searchParams.get('cv') || 'readiness';
  const setActiveSection = (key) => {
    const next = new URLSearchParams(searchParams);
    if (key === 'readiness') next.delete('cv');
    else next.set('cv', key);
    setSearchParams(next, { replace: true });
  };

  const closeout = detail.rapid.closeout || {};
  const readiness = closeout.closeoutReadiness || {};
  const closeoutState = detailState.closeout;
  const checklist = buildCloseoutChecklist(closeoutState);
  const requiredRemaining = checklist.filter((item) => item.requiredForUnblock && !item.done);
  const notAccepted = !acceptedCloseoutStatus(closeoutState.validationStatus);
  const ownerMissing = !closeoutState.handoffOwner?.trim();
  const blocked = notAccepted || ownerMissing || requiredRemaining.length > 0;
  const groups = groupCloseoutChecklist(checklist);
  const requiredGatesOpen = deriveRequiredGatesOpen(checklist).length;
  const requiredRemainingNames = requiredRemaining.map((item) => item.item);
  const readyItems = checklist.filter((item) => item.done).map((item) => item.item);
  const nextMove = blocked
    ? 'Record sponsor validation, confirm required handoff items, and preserve carry-forward lineage before final closeout.'
    : 'Record the final sponsor decision to close and hand off.';

  function updateChecklistItem(index) {
    const nextChecklist = (closeoutState.checklist || []).map((item, itemIndex) =>
      itemIndex === index ? { ...item, done: !item.done } : item,
    );
    onStateChange({
      ...detailState,
      closeout: { ...closeoutState, checklist: nextChecklist },
    });
  }

  function updateHandoffOwner(value) {
    onStateChange({
      ...detailState,
      closeout: { ...closeoutState, handoffOwner: value },
    });
  }

  const summary = {
    canClose: !blocked,
    percent: readiness.percent ?? 0,
    complete: readiness.complete ?? 0,
    total: readiness.total ?? 0,
    requiredGatesOpen,
    sponsorValidation: closeoutState.validationStatus,
    handoffOwner: closeoutState.handoffOwner,
    sustainmentOwner: closeout.sustainmentOwner,
    sustainmentCadence: closeout.sustainmentCadence,
    nextMove,
  };

  const shows = (key) => activeSection === 'all' || activeSection === key;

  return (
    <div className="rapid-tab-panel closeout-tab-panel" data-testid="rapid-closeout-tab">
      <CloseoutReadinessSummary summary={summary} />

      <CloseoutSectionNav active={activeSection} onSelect={setActiveSection} />

      <div className="closeout-views">
        <div className="closeout-view" hidden={!shows('readiness')}>
          <CloseoutReadinessView
            canClose={!blocked}
            blockers={requiredRemainingNames}
            readyItems={readyItems}
            nextMove={nextMove}
            onOpenView={setActiveSection}
          />
        </div>

        <div className="closeout-view" hidden={!shows('validation')}>
          <CloseoutValidationView
            validationStatus={closeoutState.validationStatus}
            handoffOwner={closeoutState.handoffOwner}
            onHandoffOwnerChange={updateHandoffOwner}
            onValidate={onValidate}
            validating={validating}
            routedDestination={closeoutState.routedDestination}
            blocked={blocked}
            notAccepted={notAccepted}
            ownerMissing={ownerMissing}
            requiredRemaining={requiredRemainingNames}
          />
        </div>

        <div className="closeout-view" hidden={!shows('checklist')}>
          <CloseoutChecklistView groups={groups} onToggle={updateChecklistItem} />
        </div>

        <div className="closeout-view" hidden={!shows('handoff-packet')}>
          <CloseoutHandoffPacketView
            handoffOwner={closeoutState.handoffOwner}
            sustainmentOwner={closeout.sustainmentOwner}
            sustainmentCadence={closeout.sustainmentCadence}
            carriesForward={closeout.graduation?.carriesForward}
          />
        </div>

        <div className="closeout-view" hidden={!shows('graduation-lineage')}>
          <CloseoutGraduationLineageView
            graduation={closeout.graduation}
            projectPreview={detail.projectPreviewRecord}
          >
            <RapidLineagePreview detail={detail} onOpenEvidence={onOpenEvidence} />
          </CloseoutGraduationLineageView>
        </div>

        <div className="closeout-view" hidden={!shows('lessons')}>
          <CloseoutLessonsView lessons={closeout.lessonsLearned} />
        </div>
      </div>
    </div>
  );
}

export function ShellsTab({ rapid }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState('summary');
  const shells = rapid.supportShells || rapid.roadmapStates?.shells || {};
  const structured = rapid.supportRailContent || {};
  const items = deriveShellTiers(shells, structured);
  const tiers = summarizeTiers(items);
  const liveItems = items.filter((item) => item.tier === 'Live');
  const roadmapItems = items.filter((item) => item.tier !== 'Live');

  const openTab = (tab) => setSearchParams(buildDetailSearch(searchParams, tab));
  const shows = (key) => activeSection === 'all' || activeSection === key;

  return (
    <div className="rapid-tab-panel roadmap-tab-panel" data-testid="rapid-shells-tab">
      <RoadmapSummaryPanel tiers={tiers} />

      <RoadmapSectionNav active={activeSection} onSelect={setActiveSection} />

      <div className="roadmap-views">
        <div className="roadmap-view" hidden={!shows('summary')}>
          <RoadmapSummaryView items={items} />
        </div>
        <div className="roadmap-view" hidden={!shows('live')}>
          <RoadmapCatalogView
            testId="roadmap-live-view"
            eyebrow="Live now"
            title="Capabilities you can use today"
            emptyText="No live capabilities yet."
            items={liveItems}
            onOpenTab={openTab}
          />
        </div>
        <div className="roadmap-view" hidden={!shows('roadmap')}>
          <RoadmapCatalogView
            testId="roadmap-roadmap-view"
            eyebrow="Roadmap"
            title="Planned and illustrative capabilities"
            emptyText="No roadmap items yet."
            items={roadmapItems}
            onOpenTab={openTab}
          />
        </div>
      </div>
    </div>
  );
}
